import { Container, Loader } from 'pixi.js';

import { Tween } from '../tween/Tween';
import { Scene } from './Scene';

/**
 * Scene manager state
 */
enum SceneManagerState {
  Idle,
  Loading
}

export class SceneManager<T> {
  public currentSceneName: keyof T;
  public currentScene: Scene<T[keyof T]> | null = null;
  public onStartLoading: ((sceneName: keyof T, sceneArgs: T[keyof T]) => Promise<void>) | null = null;
  public onFinishLoading: ((sceneName: keyof T, sceneArgs: T[keyof T]) => Promise<void>) | null = null;
  private readonly sceneLayer: Container;
  private readonly registeredSceneMap: Map<keyof T, any> = new Map<keyof T, any>();
  private state: SceneManagerState = SceneManagerState.Idle;

  public constructor(sceneLayer: Container) {
    if (!sceneLayer) {
      throw new Error('SceneLayer is empty.');
    }
    this.sceneLayer = sceneLayer;
  }

  /**
   * Register scene
   * @param name Scene name
   * @param scene Scene class
   */
  public registerScene<U>(name: keyof T, scene: { new (arg: U): Scene<U> }): void {
    this.registeredSceneMap.set(name, scene);
  }

  /**
   * Load scene
   * @param name Scene name
   * @param arg Scene argument
   */
  public async load<K extends keyof T>(name: K, arg: T[K]): Promise<void> {
    // state is not idle
    if (this.state !== SceneManagerState.Idle) {
      throw new Error(`Can not load '${name}' while loading other scene.`);
    }

    // scene is not registered
    const currentSceneClass = this.registeredSceneMap.get(name);
    if (!currentSceneClass) {
      throw new Error(`${name} is not registered.`);
    }

    // start loading
    this.state = SceneManagerState.Loading;

    // close prev scene
    if (this.currentScene !== null) {
      this.currentScene.interactiveChildren = false;
      await this.currentScene.close();
    }

    // start loading event
    if (this.onStartLoading) {
      await this.onStartLoading(name, arg);
    }

    // destroy prev scene
    if (this.currentScene !== null) {
      await this.currentScene.tearDown();
      this.sceneLayer.removeChild(this.currentScene);
      this.currentScene.destroy({
        children: true
      });
      this.currentScene = null;
      Tween.removeAll();
    }

    // destroy prev scene's assets
    const loader = Loader.shared;
    const deletingNames: string[] = [];
    for (const resourceName in loader.resources) {
      if (this.sessionResourceNamesIncludes(loader, resourceName)) continue;
      this.deleteResources(loader, resourceName, deletingNames);
    }
    for (const deletingName of deletingNames) {
      delete loader.resources[deletingName];
    }

    // load next scene's assets
    const typeofCurrentSceneClass = currentSceneClass as typeof Scene;
    for (const asset of typeofCurrentSceneClass.assets) {
      loader.add(asset);
    }
    await loader.loadAsync();

    // switch next scene
    this.currentSceneName = name;
    this.currentScene = new typeofCurrentSceneClass(arg);
    this.currentScene.interactiveChildren = false;
    this.sceneLayer.addChildAt(this.currentScene, 0);
    await this.currentScene.setUp();

    // finish loading event
    if (this.onFinishLoading) {
      await this.onFinishLoading(name, arg);
    }

    // open next scene
    await this.currentScene.open();
    this.currentScene.interactiveChildren = true;

    // finish loading
    this.state = SceneManagerState.Idle;
  }

  private sessionResourceNamesIncludes(loader: Loader, name: string): boolean {
    for (const sessionResourceName of loader.sessionResourceNames) {
      if (name.includes(sessionResourceName)) return true;
    }
    return false;
  }

  private deleteResources(loader: Loader, name: string, deletingNames: string[]): void {
    if (deletingNames.includes(name)) return;
    deletingNames.push(name);

    const resource = loader.resources[name];
    resource.url = '';
    resource.extension = '';
    resource.data = null;
    (resource as any).onStart.detachAll();
    (resource as any).onStart = null;
    (resource as any).onProgress.detachAll();
    (resource as any).onProgress = null;
    (resource as any).onComplete.detachAll();
    (resource as any).onComplete = null;
    (resource as any).onAfterMiddleware.detachAll();
    (resource as any).onAfterMiddleware = null;

    for (const child of resource.children) {
      this.deleteResources(loader, child.name, deletingNames);
    }
    resource.children = [];

    if (resource.texture) {
      resource.texture.destroy(true);
      delete resource.texture;
    }

    if (resource.spritesheet) {
      resource.spritesheet.destroy();
      delete resource.spritesheet;
      delete resource.textures;
    }

    if (resource.spineData) {
      delete resource.spineData;
      delete resource.spineAtlas;
    }
  }
}
